// LEHUA Collection — drawer, sort, load more, filter (Section Rendering API)
(function () {
  'use strict';

  const SECTION_SELECTOR = '[data-section-id]';

  function getSectionEl() {
    return document.querySelector('.lehua-collection-grid-section');
  }

  function getSectionId() {
    const el = getSectionEl();
    return el ? el.getAttribute('data-section-id') : null;
  }

  // === Drawer ===
  function initDrawer() {
    const drawer = document.getElementById('lehua-filters-drawer');
    const overlay = document.getElementById('lehua-filters-overlay');
    const openBtn = document.getElementById('lehua-open-filters');
    const closeBtn = document.getElementById('lehua-close-filters');
    if (!drawer || !overlay || !openBtn) return;

    let previouslyFocused = null;
    let trapHandler = null;

    function getFocusable() {
      return drawer.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
    }

    function trap(e) {
      if (e.key !== 'Tab') return;
      const items = getFocusable();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    const open = () => {
      previouslyFocused = document.activeElement;
      drawer.removeAttribute('hidden');
      overlay.removeAttribute('hidden');
      openBtn.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      // Force browser to paint hidden-removed state before adding .open class (so transform animates)
      requestAnimationFrame(() => {
        drawer.classList.add('open');
        overlay.classList.add('open');
        // Focus first interactive element in drawer (close button)
        const first = getFocusable()[0];
        if (first) first.focus(); else drawer.focus();
      });
      trapHandler = trap;
      drawer.addEventListener('keydown', trapHandler);
    };

    const close = () => {
      drawer.classList.remove('open');
      overlay.classList.remove('open');
      openBtn.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      if (trapHandler) {
        drawer.removeEventListener('keydown', trapHandler);
        trapHandler = null;
      }
      // Restore hidden after transition (250ms = --t-base)
      setTimeout(() => {
        if (!drawer.classList.contains('open')) {
          drawer.setAttribute('hidden', '');
          overlay.setAttribute('hidden', '');
        }
      }, 260);
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
        previouslyFocused = null;
      }
    };

    openBtn.addEventListener('click', open);
    if (closeBtn) closeBtn.addEventListener('click', close);
    overlay.addEventListener('click', close);

    // Escape close — only attach once per init (NOT on document, to avoid leaks)
    drawer.addEventListener('keydown', e => {
      if (e.key === 'Escape' && drawer.classList.contains('open')) close();
    });
  }

  // === Section Rendering API: fetch + replace section ===
  async function fetchAndReplaceSection(url) {
    const sectionId = getSectionId();
    if (!sectionId) return false;
    const sep = url.includes('?') ? '&' : '?';
    const fetchUrl = url + sep + 'section_id=' + encodeURIComponent(sectionId);
    try {
      const res = await fetch(fetchUrl, { headers: { 'Accept': 'text/html' } });
      if (!res.ok) throw new Error('section fetch failed: ' + res.status);
      const html = await res.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newSection = doc.querySelector('.lehua-collection-grid-section');
      const currentSection = getSectionEl();
      if (!newSection || !currentSection) throw new Error('section not found in fetched HTML');
      currentSection.replaceWith(newSection);
      // Re-init handlers on new DOM
      initDrawer();
      initSort();
      initLoadMore();
      initFilters();
      initCardCarousel();
      initSwatches();
      initSwatchColors();
      // Update URL (strip section_id from displayed URL)
      const cleanUrl = url;
      window.history.pushState({}, '', cleanUrl);
      // Scroll back to grid top so user sees results
      const newGrid = document.querySelector('.lehua-collection-grid');
      if (newGrid) newGrid.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return true;
    } catch (err) {
      console.warn('Lehua collection: section fetch failed, falling back to navigation', err);
      window.location.href = url;
      return false;
    }
  }

  // === Sort ===
  function initSort() {
    const sortSelect = document.getElementById('lehua-sort-select');
    if (!sortSelect) return;
    sortSelect.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortSelect.value);
      url.searchParams.delete('page');
      fetchAndReplaceSection(url.pathname + url.search);
    });
  }

  // === Load more ===
  function initLoadMore() {
    const btn = document.querySelector('[data-loadmore-btn]');
    if (!btn) return;
    btn.addEventListener('click', async () => {
      const nextUrl = btn.getAttribute('data-next-url');
      if (!nextUrl) return;
      const sectionId = getSectionId();
      if (!sectionId) return;
      btn.disabled = true;
      const originalText = btn.textContent;
      btn.textContent = 'Carregando...';
      try {
        const sep = nextUrl.includes('?') ? '&' : '?';
        const res = await fetch(nextUrl + sep + 'section_id=' + encodeURIComponent(sectionId), { headers: { 'Accept': 'text/html' } });
        if (!res.ok) throw new Error('loadmore fetch failed: ' + res.status);
        const html = await res.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newCards = doc.querySelectorAll('.lehua-collection-grid > .lehua-card');
        const currentGrid = document.querySelector('.lehua-collection-grid');
        if (!currentGrid) throw new Error('grid not found');
        newCards.forEach(card => currentGrid.appendChild(card));
        // Re-init carrossel + swatches on newly appended cards
        initCardCarousel(currentGrid);
        initSwatches(currentGrid);
        initSwatchColors(currentGrid);
        // Update count
        const newCount = doc.querySelector('[data-collection-count]');
        const currentCount = document.querySelector('[data-collection-count]');
        if (newCount && currentCount) currentCount.textContent = newCount.textContent;
        // Update or remove load more button
        const newBtn = doc.querySelector('[data-loadmore-btn]');
        if (newBtn) {
          btn.setAttribute('data-next-url', newBtn.getAttribute('data-next-url'));
          btn.textContent = newBtn.textContent;
          btn.disabled = false;
        } else {
          btn.parentElement.remove();
        }
        // Update URL
        window.history.pushState({}, '', nextUrl);
      } catch (err) {
        console.warn('Lehua collection: load more failed', err);
        btn.disabled = false;
        btn.textContent = originalText;
        window.location.href = nextUrl;
      }
    });
  }

  // === Filters ===
  function buildFilterUrl(form) {
    const params = new URLSearchParams();
    for (const [key, value] of new FormData(form).entries()) {
      if (value !== '' && value != null) params.append(key, value);
    }
    return window.location.pathname + (params.toString() ? '?' + params.toString() : '');
  }

  function initFilters() {
    const form = document.getElementById('lehua-filters-form');
    const clearBtn = document.getElementById('lehua-filters-clear');
    const applyBtn = document.getElementById('lehua-filters-apply');
    if (!form) return;

    // --- Accordion: recolher/expandir cada grupo ---
    form.querySelectorAll('[data-filter-toggle]').forEach(head => {
      head.addEventListener('click', () => {
        const group = head.closest('.lehua-drawer__group');
        if (!group) return;
        const collapsed = group.classList.toggle('is-collapsed');
        head.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
      });
    });

    // --- Contagem ao vivo no botão "Ver N produtos" + badge por grupo ---
    let countTimer = null;
    async function updateLiveCount() {
      // badge por grupo (conta selecionados localmente)
      form.querySelectorAll('.lehua-drawer__group').forEach(group => {
        const badge = group.querySelector('[data-group-count]');
        if (!badge) return;
        const n = group.querySelectorAll('input[type="checkbox"]:checked').length;
        badge.textContent = n;
        if (n > 0) badge.removeAttribute('hidden'); else badge.setAttribute('hidden', '');
      });
      // total de produtos (via Section Rendering API)
      const sectionId = getSectionId();
      if (!sectionId || !applyBtn) return;
      const url = buildFilterUrl(form);
      const sep = url.includes('?') ? '&' : '?';
      try {
        applyBtn.classList.add('is-loading');
        const res = await fetch(url + sep + 'section_id=' + encodeURIComponent(sectionId), { headers: { 'Accept': 'text/html' } });
        if (!res.ok) throw new Error('count fetch failed');
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const countEl = doc.querySelector('[data-collection-count]');
        const m = countEl ? (countEl.textContent.match(/\d+/) || [])[0] : null;
        applyBtn.textContent = m ? ('Ver ' + m + ' produto' + (m === '1' ? '' : 's')) : 'Ver produtos';
      } catch (err) {
        applyBtn.textContent = 'Ver produtos';
      } finally {
        applyBtn.classList.remove('is-loading');
      }
    }
    function scheduleCount() {
      window.clearTimeout(countTimer);
      countTimer = window.setTimeout(updateLiveCount, 280);
    }

    form.addEventListener('change', scheduleCount);
    form.addEventListener('input', e => { if (e.target.type === 'number') scheduleCount(); });
    updateLiveCount(); // estado inicial

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      fetchAndReplaceSection(buildFilterUrl(form));
    });

    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        form.querySelectorAll('input[type="checkbox"]').forEach(cb => { cb.checked = false; });
        form.querySelectorAll('input[type="number"]').forEach(inp => { inp.value = ''; });
        updateLiveCount();
      });
    }
  }

  // === Swatch híbrido: cor desconhecida (#999) => pinta bolinha SÓLIDA com a cor média da foto ===
  function initSwatchColors(scope) {
    const root = scope || document;
    root.querySelectorAll('.lehua-card__swatch[data-swatch-needs-color]').forEach(sw => {
      if (sw.dataset.colorSampled === '1') return;
      const src = sw.dataset.swatchImageSrc;
      if (!src) return;
      sw.dataset.colorSampled = '1';
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const cw = 30, ch = 40;
          const cv = document.createElement('canvas');
          cv.width = cw; cv.height = ch;
          const cx = cv.getContext('2d', { willReadFrequently: true });
          cx.drawImage(img, 0, 0, cw, ch);
          // amostra só a região central (evita o fundo bege nas bordas)
          const x0 = Math.floor(cw * 0.3), y0 = Math.floor(ch * 0.35);
          const w = Math.ceil(cw * 0.4), h = Math.ceil(ch * 0.35);
          const d = cx.getImageData(x0, y0, w, h).data;
          let r = 0, g = 0, b = 0, n = 0;
          for (let i = 0; i < d.length; i += 4) {
            if (d[i + 3] < 128) continue; // ignora pixels transparentes
            r += d[i]; g += d[i + 1]; b += d[i + 2]; n++;
          }
          if (!n) return;
          sw.style.background = 'rgb(' + Math.round(r / n) + ',' + Math.round(g / n) + ',' + Math.round(b / n) + ')';
        } catch (e) { /* foto bloqueada por CORS: mantém o cinza de fallback */ }
      };
      img.src = src;
    });
  }

  // === Card carousel: swipe (mobile) + setas (desktop) + dots, filtrável por cor ===
  function initCardCarousel(scope) {
    const root = scope || document;
    root.querySelectorAll('.lehua-card').forEach(card => {
      const track = card.querySelector('[data-card-carousel]');
      if (!track || track.dataset.carouselInited === '1') return;
      track.dataset.carouselInited = '1';
      const slides = Array.from(track.querySelectorAll('.lehua-card__slide'));
      const prev = card.querySelector('[data-card-prev]');
      const next = card.querySelector('[data-card-next]');
      const dotsWrap = card.querySelector('[data-card-dots]');

      const visible = () => slides.filter(s => s.style.display !== 'none');

      function buildDots() {
        if (!dotsWrap) return;
        const vis = visible();
        dotsWrap.innerHTML = '';
        if (vis.length <= 1) return;
        vis.forEach(() => {
          const d = document.createElement('span');
          d.className = 'lehua-card__dot';
          dotsWrap.appendChild(d);
        });
        syncUI();
      }

      function currentIndex() {
        const w = track.clientWidth || 1;
        return Math.round(track.scrollLeft / w);
      }

      function syncUI() {
        const idx = currentIndex();
        const count = visible().length;
        if (dotsWrap) {
          dotsWrap.querySelectorAll('.lehua-card__dot').forEach((d, i) => {
            d.classList.toggle('is-active', i === idx);
          });
        }
        if (prev) prev.hidden = idx <= 0;
        if (next) next.hidden = idx >= count - 1;
      }

      function scrollByOne(dir) {
        const w = track.clientWidth;
        track.scrollBy({ left: dir * w, behavior: 'smooth' });
      }

      if (prev) prev.addEventListener('click', e => { e.preventDefault(); e.stopPropagation(); scrollByOne(-1); });
      if (next) next.addEventListener('click', e => { e.preventDefault(); e.stopPropagation(); scrollByOne(1); });
      track.addEventListener('scroll', () => {
        window.clearTimeout(track._lehuaScrollT);
        track._lehuaScrollT = window.setTimeout(syncUI, 80);
      });

      // Filtro por cor: mostra só as fotos cujo ALT bate com a cor; sem match => mostra todas.
      card._lehuaFilterColor = function (color) {
        const norm = (color || '').toLowerCase().trim();
        let any = false;
        slides.forEach(s => { if (norm && (s.dataset.imgColor || '').trim() === norm) any = true; });
        slides.forEach(s => {
          const match = norm && (s.dataset.imgColor || '').trim() === norm;
          s.style.display = (!any || match) ? '' : 'none';
        });
        track.scrollTo({ left: 0, behavior: 'auto' });
        buildDots();
      };

      // Estado inicial: filtra pela cor ativa (se houver) ou só monta os dots
      const active = card.querySelector('.lehua-card__swatch--active');
      if (active) card._lehuaFilterColor(active.dataset.swatchColor);
      else buildDots();
    });
  }

  // === Card swatches: click → filtra carrossel por cor + atualiza link/estoque ===
  function initSwatches(scope) {
    const root = scope || document;
    root.querySelectorAll('.lehua-card').forEach(card => {
      const swatches = card.querySelectorAll('.lehua-card__swatch[data-swatch-color]');
      if (!swatches.length) return;
      const link = card.querySelector('.lehua-card__link');
      const stockEl = card.querySelector('[data-card-stock]');

      const sizeEls = card.querySelectorAll('.lehua-card__size[data-size]');

      function renderStock(sw) {
        if (!stockEl || !sw) return;
        const n = parseInt(sw.dataset.swatchStock || '0', 10);
        stockEl.classList.remove('is-low', 'is-out');
        if (isNaN(n) || n <= 0) {
          stockEl.textContent = 'Esgotado';
          stockEl.classList.add('is-out');
        } else if (n <= 5) {
          stockEl.textContent = 'Últimas ' + n + ' unidades';
          stockEl.classList.add('is-low');
        } else if (n < 10) {
          stockEl.textContent = n + ' em estoque';
        } else {
          stockEl.textContent = '';
        }
      }

      function updateSizes(sw) {
        if (!sizeEls.length || !sw) return;
        const avail = (sw.dataset.swatchSizes || '').split(',').filter(Boolean);
        sizeEls.forEach(sp => {
          sp.classList.toggle('lehua-card__size--out', avail.indexOf(sp.dataset.size) === -1);
        });
      }

      swatches.forEach(s => {
        if (s.dataset.swatchInited === '1') return;
        s.dataset.swatchInited = '1';
        s.addEventListener('click', e => {
          e.preventDefault();
          e.stopPropagation();
          const href = s.dataset.swatchHref;
          // Filtra o carrossel pelas fotos da cor escolhida
          if (typeof card._lehuaFilterColor === 'function') {
            card._lehuaFilterColor(s.dataset.swatchColor);
          }
          if (link && href) link.href = href;
          swatches.forEach(o => o.classList.remove('lehua-card__swatch--active'));
          s.classList.add('lehua-card__swatch--active');
          renderStock(s);
          updateSizes(s);
        });
      });

      // Estado inicial = cor ativa (ou a primeira)
      const activeSwatch = card.querySelector('.lehua-card__swatch--active') || swatches[0];
      renderStock(activeSwatch);
      updateSizes(activeSwatch);
    });
  }

  function init() {
    initDrawer();
    initSort();
    initLoadMore();
    initFilters();
    initCardCarousel();
    initSwatches();
    initSwatchColors();
  }

  // Re-init after Section Rendering API replaces grid
  document.addEventListener('lehua:grid-updated', () => { initCardCarousel(); initSwatches(); initSwatchColors(); });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
